import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CONSENT_KEY = 'nauticoach_cookie_consent';

export type ConsentChoice = 'accepted' | 'refused' | null;

export const getConsentChoice = (): ConsentChoice => {
  const value = localStorage.getItem(CONSENT_KEY);
  if (value === 'accepted' || value === 'refused') return value;
  return null;
};

export const hasTrackingConsent = (): boolean => {
  return getConsentChoice() === 'accepted';
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    const consent = getConsentChoice();
    if (!consent) {
      // Petit délai pour ne pas gêner le chargement initial
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setVisible(false);
    // Recharger pour activer le tracking
    window.location.reload();
  };

  const handleRefuse = () => {
    localStorage.setItem(CONSENT_KEY, 'refused');
    setVisible(false);
    // Nettoyer les données de session existantes
    sessionStorage.removeItem('nauticoach_session_id');
    sessionStorage.removeItem('nauticoach_session_start');
    sessionStorage.removeItem('nauticoach_page_count');
    sessionStorage.removeItem('nauticoach_last_visit_id');
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 lg:bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom duration-500">
      <div className="max-w-4xl mx-auto bg-card border border-border rounded-xl shadow-2xl p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-nautic/10 rounded-full flex items-center justify-center">
            <Cookie className="w-5 h-5 text-nautic" />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-nautic" />
              <h3 className="font-semibold text-foreground text-sm">{t('cookies.title')}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('cookies.text')}{' '}
              <Link to="/politique-confidentialite" className="text-nautic underline hover:no-underline">
                {t('cookies.more')}
              </Link>
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleAccept} size="sm" className="bg-nautic-gold hover:bg-nautic-gold/90 text-nautic-dark font-semibold">
                {t('cookies.accept')}
              </Button>
              <Button onClick={handleRefuse} variant="outline" size="sm">
                {t('cookies.refuse')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
